import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAtomValue } from 'jotai';
import useActionDuration from '../../../../../common/hooks/useActionDuration';
import { getAnnotationStyle } from '../../../../../common/utils/utils';
import { isVideoLabelingM4EnabledAtom } from '../../../../labelling-v2/atoms/m4-atoms';
import { useStageAtoms } from '../../../../labelling-v2/components/stage-scope/hooks';
import { useDynamicAttributesText } from '../../../../labelling-v2/hooks/use-dynamic-attributes-text';
import type { ANNOTATION_SHAPE_STYLING_TYPE } from '../../../constants';
import { ANNOTATION_TYPE } from '../../../constants';
import { STAGE_EVENTS } from '../../../constants';
import useStage from '../../../providers/hooks/use-stage';
import useTutorial from '../../../providers/hooks/use-tutorials';
import { type StagePointerEventT } from '../../../providers/stage-provider';
import { POLYLINE_TUTORIAL_CONFIG } from '../../../tutorials/polyline-tutorial';
import { TOOLBAR_TUTORIAL } from '../../../tutorials/toolbar-tutorial';
import { type PolylineAnnotationT } from '../../../types';
import { isLabelingMode } from '../../toolbar/utils';
import { GhostPointRenderer } from '../ghost-point-renderer/ghost-point-renderer';
import { convertDeltaToMousePosition } from '../rectangle/utils';
import Shape from '../shape/shape';
import type { VertexT } from '../shape/vertex';
import { VERTEX_TYPE } from '../shape/vertex';
import { clampPointToStageBounds, constrainPointsToStageBounds, getPointDistance, getStylesForStylingType, getTransformedPolyPointsOnVertexMove, getTranslatedPolyPoints } from '../utils';
import { ensureAnnotationWithinMultiSelectBox } from '../utils';
import { getArrowTransformationProperties } from './utils';
const POLYLINE_TOOLBAR_TUTORIAL_ID = 'select-tool-POLYLINE';
type PropsT = {
  isDrawing?: boolean;
  annotation?: PolylineAnnotationT;
  annotationIdx?: string;
  disableChanges?: boolean;
  stylingType?: (typeof ANNOTATION_SHAPE_STYLING_TYPE)[keyof typeof ANNOTATION_SHAPE_STYLING_TYPE];
};
const REPEAT_POINT_DELTA = 10;
const Polyline = ({
  isDrawing,
  annotation,
  annotationIdx,
  disableChanges,
  stylingType
}: PropsT) => {
  const {
    stage,
    addAnnotation,
    updateAnnotation,
    deleteAnnotation,
    getStageCenter,
    multiSelectBoxChange,
    multiSelectBox,
    getStageDimension
  } = useStage();
  const {
    editorModeAtom,
    editableAnnotationAtom,
    focusedAnnotationIdAtom,
    selectedAnnotationIdsAtom,
    selectedAnnotationLabelAtom
  } = useStageAtoms();
  const editorMode = useAtomValue(editorModeAtom);
  const editableAnnotation = useAtomValue(editableAnnotationAtom);
  const focusedAnnotationId = useAtomValue(focusedAnnotationIdAtom);
  const selectedAnnotationIds = useAtomValue(selectedAnnotationIdsAtom);
  const annotationLabel = useAtomValue(selectedAnnotationLabelAtom);
  const isVideoLabelingM4Enabled = useAtomValue(isVideoLabelingM4EnabledAtom);
  const polylineRef = useRef<SVGPolylineElement | null>(null);
  const [labelCoords, setLabelCoords] = useState<[number, number] | null>(null);
  const [drawingPolyline, setDrawingPolyline] = useState<PolylineAnnotationT | null>(annotation ?? null);
  const {
    changeWidth,
    changeHeight,
    selectedMultiBoxVertexType
  } = multiSelectBoxChange || {};
  const [selectedVertexIdx, setSelectedVertexIdx] = useState<number | null>(null);
  const isSelected = typeof annotationIdx === 'string' && selectedAnnotationIds.indexOf(annotationIdx) >= 0;
  const {
    start,
    end
  } = useActionDuration();
  const isLocked = Boolean(annotation?.locked);

  // move point is used as we can't use points array because of race condition with pointer down event
  const [tempMovePoint, setTempMovePoint] = useState<[number, number] | null>(null);
  const isEditable = editableAnnotation === annotationIdx;
  const isFocused = focusedAnnotationId === annotationIdx;
  const [ghostVertex, setGhostVertex] = useState<VertexT | null>(null);
  const {
    addStep,
    next,
    currentActiveTutorial
  } = useTutorial();
  useEffect(() => {
    if (!annotation || !isSelected || annotationIdx === undefined || !drawingPolyline || disableChanges || isLocked) {
      return;
    }
    if (typeof changeWidth === 'number' && typeof changeHeight === 'number' && selectedMultiBoxVertexType && typeof VERTEX_TYPE[selectedMultiBoxVertexType] === 'string') {
      const [x, y] = convertDeltaToMousePosition({
        x: 0,
        y: 0,
        deltaWidth: changeWidth,
        deltaHeight: changeHeight,
        vertexType: selectedMultiBoxVertexType
      });
      const newPoints = getTranslatedPolyPoints(x, y, annotation.data.points);
      let newPolyline = {
        ...drawingPolyline,
        data: {
          points: newPoints
        }
      };
      if (multiSelectBox) {
        // @ts-expect-error Type 'RectangleAnnotationT | PolygonAnnotationT | PointAnnotationT | EllipseAnnotationT | PolylineAnnotationT...
        newPolyline = ensureAnnotationWithinMultiSelectBox(newPolyline, multiSelectBox);
      }

      // eslint-disable-next-line react-hooks/set-state-in-effect
      setDrawingPolyline(newPolyline);
      start();
    }
  }, [changeWidth, changeHeight, selectedMultiBoxVertexType, annotation, drawingPolyline, multiSelectBox, disableChanges, isLocked, isSelected, annotationIdx, start]);
  useEffect(() => {
    const rootTutorialId = TOOLBAR_TUTORIAL[POLYLINE_TOOLBAR_TUTORIAL_ID]?.id;
    if (isDrawing && currentActiveTutorial === rootTutorialId && isLabelingMode(editorMode)) {
      rootTutorialId && addStep({
        ...POLYLINE_TUTORIAL_CONFIG.drawPolyline,
        getTarget: () => polylineRef.current
      }, rootTutorialId);
    }
  }, [isDrawing, addStep, currentActiveTutorial, editorMode]);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setDrawingPolyline(annotation ?? null);
  }, [annotation]);
  useEffect(() => {
    if (polylineRef.current && !isDrawing) {
      const box = polylineRef.current.getBBox();
      setLabelCoords([box.x + box.width / 2, box.y + box.height / 2]);
    }
  }, [isDrawing, drawingPolyline]);
  const onPointerDown = useCallback(
  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  (stageEvent: StagePointerEventT) => {
    const {
      mousePos
    } = stageEvent.detail;
    // TODO: handle case where annotationLabel is not selected
    if (!annotationLabel) return;
    const points = drawingPolyline?.data.points ? [...drawingPolyline.data.points] : [];
    if (points.length === 0) {
      start();
    }
    if (points.length === 0) {
      next(POLYLINE_TUTORIAL_CONFIG.drawPolyline.id);
      const rootTutorialId = TOOLBAR_TUTORIAL['select-tool-POLYLINE']?.id;
      rootTutorialId && addStep({
        ...POLYLINE_TUTORIAL_CONFIG.endPolyline,
        getTarget: () => polylineRef.current
      }, rootTutorialId);
    }

    // Clamp the point to stage bounds to prevent negative or out-of-bounds coordinates
    const [stageWidth, stageHeight] = getStageDimension();
    const clampedPoint = clampPointToStageBounds(mousePos, stageWidth, stageHeight);

    // set the drawing polyline
    points.push(clampedPoint);
    const polyline: PolylineAnnotationT = {
      annotationType: ANNOTATION_TYPE.POLYLINE,
      annotationLabel,
      data: {
        points
      }
    };
    setDrawingPolyline(polyline);
  }, [annotationLabel, drawingPolyline?.data.points, start, next, addStep, getStageDimension]);
  const onPointerMove = useCallback((stageEvent: StagePointerEventT) => {
    if (!drawingPolyline) return;
    const {
      mousePos,
      shiftKey,
      mouseLeftDown,
      scale
    } = stageEvent.detail;
    let points = [...drawingPolyline.data.points];
    const lastCommittedPoint = points[points.length - 1];

    // Clamp mouse position to stage bounds
    const [stageWidth, stageHeight] = getStageDimension();
    const clampedMousePos = clampPointToStageBounds(mousePos, stageWidth, stageHeight);

    // Calculate delta from last committed point
    // @ts-expect-error todo(ts-migration) TS2345 Argument of type '[number, number] | undefined' is not assignable to parameter of type 'MousePosT'.
    const distance = getPointDistance(clampedMousePos, lastCommittedPoint);

    // TODO: remove shiftKey and use only left pointer to add continuous points
    // Add point before temp move point
    const scaledRepeatPointDelta = REPEAT_POINT_DELTA / scale; // calculate delta based on scale
    if ((shiftKey || mouseLeftDown) && distance > scaledRepeatPointDelta) {
      points.push(clampedMousePos);
      setDrawingPolyline({
        ...drawingPolyline,
        data: {
          points
        }
      });
    }
    setTempMovePoint(clampedMousePos);
  }, [drawingPolyline, getStageDimension]);
  const onDoubleCLick = useCallback((_stageEvent: StagePointerEventT) => {
    if (drawingPolyline) {
      addAnnotation(drawingPolyline, {
        actionDuration: end(),
        vertex: drawingPolyline.data.points.length
      });
      setDrawingPolyline(null);
      setTempMovePoint(null);
      next(POLYLINE_TUTORIAL_CONFIG.endPolyline.id);
    }
  }, [addAnnotation, drawingPolyline, end, next]);
  useEffect(() => {
    if (!stage || !isLabelingMode(editorMode)) {
      return;
    }
    if (isDrawing) {
      stage.addEventListener('pointerdown', onPointerDown);
      stage.addEventListener('pointermove', onPointerMove);
      stage.addEventListener('doubleclick', onDoubleCLick);
      return () => {
        stage.removeEventListener('pointerdown', onPointerDown);
        stage.removeEventListener('pointermove', onPointerMove);
        stage.removeEventListener('doubleclick', onDoubleCLick);
      };
    }
  }, [isDrawing, stage, onPointerDown, onPointerMove, onDoubleCLick, editorMode]);
  const onDrag = useCallback((stageEvent: StagePointerEventT) => {
    if (annotation) {
      if (selectedVertexIdx === null && stageEvent.detail.dragTarget !== polylineRef.current) {
        return;
      }
      const [stageWidth, stageHeight] = getStageDimension();
      const newPoints = selectedVertexIdx !== null ? getTransformedPolyPointsOnVertexMove(stageEvent.detail.mousePos, annotation.data.points, selectedVertexIdx) : getTranslatedPolyPoints(stageEvent.detail.deltaX, stageEvent.detail.deltaY, annotation.data.points);

      // Constrain all points to stay within stage bounds during drag
      const {
        points: constrainedPoints
      } = constrainPointsToStageBounds(newPoints, stageWidth, stageHeight);
      setDrawingPolyline({
        ...annotation,
        data: {
          points: constrainedPoints
        }
      });
      start();
    }
  }, [selectedVertexIdx, annotation, start, getStageDimension]);
  const onDragEnd = useCallback((stageEvent: StagePointerEventT) => {
    if (annotation && annotationIdx !== undefined) {
      if (selectedVertexIdx === null && stageEvent.detail.dragTarget !== polylineRef.current) {
        return;
      }
      const [stageWidth, stageHeight] = getStageDimension();
      const newPoints = selectedVertexIdx !== null ? getTransformedPolyPointsOnVertexMove(stageEvent.detail.mousePos, annotation.data.points, selectedVertexIdx) : getTranslatedPolyPoints(stageEvent.detail.deltaX, stageEvent.detail.deltaY, annotation.data.points);

      // Constrain all points to stay within stage bounds during drag
      const {
        points: constrainedPoints
      } = constrainPointsToStageBounds(newPoints, stageWidth, stageHeight);
      updateAnnotation(annotationIdx, {
        ...annotation,
        data: {
          points: constrainedPoints
        }
      }, {
        actionDuration: end(),
        vertex: constrainedPoints.length
      });
      setSelectedVertexIdx(null);
    }
  }, [selectedVertexIdx, annotation, annotationIdx, updateAnnotation, end, getStageDimension]);
  const onVertexRightClick = useCallback(() => {
    console.error('isDrawing', isDrawing);
    if (isDrawing) {
      const points = drawingPolyline?.data.points ? [...drawingPolyline.data.points] : [];
      if (!annotationLabel) {
        console.error('annotationLabel is undefined, cannot create polyline.');
        return;
      }
      points.pop();
      if (points.length === 0) {
        setDrawingPolyline(null);
        return;
      }
      const polyline: PolylineAnnotationT = {
        annotationType: ANNOTATION_TYPE.POLYLINE,
        annotationLabel,
        data: {
          points
        }
      };
      setDrawingPolyline(polyline);
    } else if (selectedVertexIdx !== null) {
      if (!annotation || !annotationIdx) return;
      const originalPoints = annotation.data.points;
      const newPoints = originalPoints.slice(0, selectedVertexIdx).concat(originalPoints.slice(selectedVertexIdx + 1, originalPoints.length));
      if (newPoints.length === 1) {
        deleteAnnotation(annotationIdx, {
          actionDuration: end()
        });
        return;
      }
      updateAnnotation(annotationIdx, {
        ...annotation,
        data: {
          points: newPoints
        }
      }, {
        actionDuration: end(),
        vertex: newPoints.length
      });
      setSelectedVertexIdx(null);
    }
  }, [isDrawing, selectedVertexIdx, drawingPolyline, annotationLabel, annotation, updateAnnotation, annotationIdx, end, deleteAnnotation]);
  useEffect(() => {
    if (!stage || disableChanges || isLocked || !isLabelingMode(editorMode)) return;
    if (isEditable) {
      stage.addEventListener('drag', onDrag);
      stage.addEventListener('dragend', onDragEnd);
      return () => {
        stage.removeEventListener('drag', onDrag);
        stage.removeEventListener('dragend', onDragEnd);
      };
    }
  }, [isEditable, stage, onDrag, onDragEnd, disableChanges, editorMode, isLocked]);

  // Added to ensure that event listener is registered everytime isEditbale & selectedVertexIdx
  // change so that we can access the right annotation in onVertexRightClick callback
  useEffect(() => {
    if (!stage || !isLabelingMode(editorMode)) return;
    stage.addEventListener(STAGE_EVENTS.RIGHT_CLICK, onVertexRightClick);
    return () => {
      stage.removeEventListener(STAGE_EVENTS.RIGHT_CLICK, onVertexRightClick);
    };
  }, [stage, onVertexRightClick, editorMode]);
  const onAddNewPoint = useCallback((newPoints: [number, number][]) => {
    if (!annotationIdx || !annotation) {
      return;
    }
    updateAnnotation(annotationIdx, {
      ...annotation,
      data: {
        points: newPoints
      }
    }, {
      actionDuration: end(),
      vertex: newPoints.length
    });
  }, [annotation, annotationIdx, end, updateAnnotation]);
  const polylineStyle = useMemo(() => getAnnotationStyle({
    fill: drawingPolyline?.annotationLabel.style.fillColor,
    fillOpacity: 0,
    stroke: drawingPolyline?.annotationLabel.style.strokeColor,
    ...getStylesForStylingType(stylingType)
  }, {
    isFocused,
    isLocked,
    useStageStrokeWidth: true,
    overrideOpacity: true,
    isM4: isVideoLabelingM4Enabled
  }), [drawingPolyline?.annotationLabel.style.fillColor, drawingPolyline?.annotationLabel.style.strokeColor, stylingType, isFocused, isLocked, isVideoLabelingM4Enabled]);
  const edgeRenderer = useCallback(({
    points,
    onMouseEnter,
    onMouseLeave,
    onClick
  }: {
    points: string;
    onMouseEnter?: React.MouseEventHandler<SVGPolylineElement>;
    onMouseLeave?: React.MouseEventHandler<SVGPolylineElement>;
    onClick?: React.MouseEventHandler<SVGPolylineElement>;
  }) => {
    return <polyline points={points} style={{
      ...polylineStyle,
      fill: 'none',
      cursor: 'pointer'
    }} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} onClick={onClick} data-stage-custom />;
  }, [polylineStyle]);
  const dynamicAttributesText = useDynamicAttributesText(annotationIdx);
  if (!drawingPolyline) {
    const stageCenter = getStageCenter();
    return <polyline ref={polylineRef} points={`${stageCenter[0]},${stageCenter[1]}`} style={polylineStyle}></polyline>;
  }
  const points = drawingPolyline.data.points;
  let polylinePoints = points.map(([x, y]) => `${x},${y}`).join(' ');
  if (isDrawing && tempMovePoint) {
    polylinePoints += ` ${tempMovePoint[0]},${tempMovePoint[1]}`;
  }
  return <Shape vertices={points.map(point => ({
    point,
    type: VERTEX_TYPE.MOVE
  }))} isLocked={isLocked} annotationLabel={{
    ...drawingPolyline.annotationLabel,
    labelCoords
  }} isDrawing={isDrawing} annotationIdx={annotationIdx} metadataPillText={dynamicAttributesText} onVertexSelect={(_, idx) => setSelectedVertexIdx(idx)} ghostVertices={ghostVertex ? [ghostVertex] : []} dataAttributes={{
    'data-e2e-id': `annotation-${drawingPolyline.annotationLabel.title}-${drawingPolyline.annotationType}`,
    'data-annotation-id': annotationIdx?.toString() || ''
  }}>
      <polyline ref={polylineRef} points={polylinePoints} style={polylineStyle} {...!isDrawing ? {
      markerStart: 'url(#markerStart)',
      markerEnd: 'url(#markerEnd)'
    } : {}}></polyline>
      {/* Arrow positioned at the midpoint and rotated according to the polyline's direction */}
      {!isDrawing && <polygon points="0,-10 20,0 0,10" fill="white" transform={getArrowTransformationProperties(points)} />}
      {isLabelingMode(editorMode) && <GhostPointRenderer<SVGPolylineElement> isSelected={isEditable} isDrawing={isDrawing} points={points} ghostVertex={ghostVertex} edgeRenderer={edgeRenderer} onAddNewPoints={onAddNewPoint} onGhostVertexChange={setGhostVertex} />}
    </Shape>;
};
export default Polyline;